import { PlayerPublic } from "@aura-frontend/omnibus-az-client";
import { IntersectionTrigger } from "@aura-frontend/web";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CirclePlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";

import { useAvailablePlayers } from "@/hooks/useAvailablePlayers";
import { capitalize } from "@/utils/capitalize";
import { splitName } from "@/utils/splitName";

import SearchInput from "../ui/SearchInput";
import { Spinner } from "../ui/spinner";

type SelectablePlayersListProps = {
  organizationId: string;
  enablePlayersQuery: boolean;
  selectedPlayers: PlayerPublic[];
  onPlayerSelectionChange: (player: PlayerPublic, checked: boolean) => void;
  /**
   * A function that returns true if the player should be included in the list.
   * It can be used to filter out players that are not supposed to be visible in some use cases.
   * If it's not provided, all players will be shown.
   * @param player - The player to filter.
   * @returns true if the player should be included in the list, false otherwise.
   */
  filterFn?: (player: PlayerPublic) => boolean;
  pinnedPlayers?: PlayerPublic[];
  pendingPlayerIds?: Set<string>;
  onPendingPlayersHydrated?: (players: PlayerPublic[]) => void;
};

export const SelectablePlayersList = ({
  organizationId,
  enablePlayersQuery,
  selectedPlayers,
  onPlayerSelectionChange,
  filterFn,
  pinnedPlayers,
  pendingPlayerIds,
  onPendingPlayersHydrated,
}: SelectablePlayersListProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [continueString, setContinueString] = useState<string>("");

  const {
    playersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useAvailablePlayers({
    organizationId,
    continueString,
    debouncedSearchValue,
    enabled: enablePlayersQuery,
  });

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    500,
    [searchValue],
  );

  const pinnedIds = useMemo(
    () =>
      new Set(
        (pinnedPlayers ?? []).map((p) => p.external_ids?.[0]).filter(Boolean),
      ),
    [pinnedPlayers],
  );

  const composedFilter = useMemo(
    () => (player: PlayerPublic) =>
      !pinnedIds.has(player.external_ids?.[0] ?? "") &&
      (filterFn?.(player) ?? true),
    [pinnedIds, filterFn],
  );

  const filteredPlayers: PlayerPublic[] = useMemo(
    () =>
      playersData?.pages
        .flatMap((page) => page.players)
        .filter(composedFilter) ?? [],
    [playersData?.pages, composedFilter],
  );

  const filteredPinnedPlayers: PlayerPublic[] = useMemo(
    () =>
      pinnedPlayers?.filter((p) => {
        const name = p.known_names?.[0] ?? "";
        return (
          !debouncedSearchValue ||
          name.toLowerCase().includes(debouncedSearchValue.toLowerCase())
        );
      }) ?? [],
    [pinnedPlayers, debouncedSearchValue],
  );

  const renderPlayerRow = (player: PlayerPublic) => {
    const { firstName, lastName } = splitName(player.known_names?.[0] ?? "");
    const isSelected = selectedPlayers.some(
      (p) => p.external_ids?.[0] === player.external_ids?.[0],
    );
    const jerseyNumber =
      (
        player as unknown as {
          jerseyNumber?: number | string;
          number?: number | string;
        }
      ).jerseyNumber ??
      (player as unknown as { number?: number | string }).number ??
      "-";

    // Handle both naming conventions: pitch_side/hit_side (search API) and throws/bats (roster API)
    const playerThrows =
      (player as unknown as { throws?: string }).throws || player.pitch_side;
    const playerBats =
      (player as unknown as { bats?: string }).bats || player.hit_side;

    const throws = playerThrows ? capitalize(playerThrows) : "-";
    const bats = playerBats ? capitalize(playerBats) : "-";

    return (
      <div
        key={player.external_ids?.[0]}
        className="flex items-center border-b border-gray-200 py-2"
      >
        <div className="flex-1 text-sm">
          {firstName} {lastName}
        </div>
        <div className="w-20 text-sm text-center">{jerseyNumber}</div>
        <div className="w-20 text-sm text-center">{throws}</div>
        <div className="w-20 text-sm text-center">{bats}</div>
        {}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPlayerSelectionChange(player, !isSelected);
          }}
          className="rounded p-1 transition-colors hover:bg-gray-100"
          aria-label={isSelected ? "Remove player" : "Add player"}
        >
          {isSelected ? (
            <XMarkIcon className="h-5 w-5 text-accent-dark-3" />
          ) : (
            <CirclePlus className="h-5 w-5 text-blue-500" />
          )}
        </button>
      </div>
    );
  };

  // Auto-select pending players when they appear in the loaded list
  useEffect(() => {
    if (!pendingPlayerIds || pendingPlayerIds.size === 0) {
      return;
    }

    const allLoadedPlayers = [...filteredPinnedPlayers, ...filteredPlayers];
    const matchedPlayers = allLoadedPlayers.filter((p) => {
      const playerId = p.external_ids?.[0];
      return pendingPlayerIds.has(playerId ?? "");
    });

    if (matchedPlayers.length > 0) {
      onPendingPlayersHydrated?.(matchedPlayers);
    }
  }, [
    filteredPlayers,
    filteredPinnedPlayers,
    pendingPlayerIds,
    onPendingPlayersHydrated,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm">
        {pinnedPlayers?.length
          ? "Select players for this roster"
          : "Players available to add"}
      </div>
      <SearchInput
        value={searchValue}
        onSearch={(value) => {
          setSearchValue(value);
          setContinueString("");
        }}
      />
      <div className="flex items-center border-b border-gray-300 py-2 font-semibold text-xs text-gray-600 pr-2">
        <div className="flex-1">NAME</div>
        <div className="w-20">JERSEY #</div>
        <div className="w-20">THROWS</div>
        <div className="w-20">BATS</div>
        <div className="w-8"></div>
      </div>
      <div className="h-[300px] overflow-auto pr-2">
        {status === "pending" &&
          filteredPlayers.length === 0 &&
          filteredPinnedPlayers.length === 0 && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}

        {status === "error" && (
          <div className="text-center mt-2 text-red-600">{String(error)}</div>
        )}

        {status === "success" && (
          <>
            {filteredPinnedPlayers.length === 0 &&
            filteredPlayers.length === 0 ? (
              <p className="text-center mt-2 text-sm">No players found.</p>
            ) : (
              <div className="flex flex-col">
                {filteredPinnedPlayers.length > 0 && (
                  <div className="flex flex-col">
                    <div className="text-sm font-bold text-gray-700 py-2">
                      On This Roster
                    </div>
                    {filteredPinnedPlayers.map(renderPlayerRow)}
                  </div>
                )}

                {filteredPlayers.length > 0 && (
                  <div className="flex flex-col">
                    {filteredPinnedPlayers.length > 0 && (
                      <div className="text-sm font-bold text-gray-700 pt-4 pb-2">
                        Other Players In Org
                      </div>
                    )}
                    {filteredPlayers.map(renderPlayerRow)}
                    {hasNextPage && (
                      <IntersectionTrigger
                        data-testid="intersection-trigger"
                        onIntersect={handleLoadMore}
                      >
                        <div className="w-full flex justify-center py-2">
                          {isFetchingNextPage && <Spinner />}
                        </div>
                      </IntersectionTrigger>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
